import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { StructuredData } from "@/components/StructuredData";
import { SiteLayout } from "@/components/site/SiteLayout";
import { supabase } from "@/integrations/supabase/client";
import { stripRichTextToPlainText } from "@/lib/richText";

interface BlogPostListItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  meta_description: string | null;
  cover_image_url: string | null;
  author_name: string | null;
  published_at: string | null;
  created_at: string;
  content: string;
}

const PAGE_SIZE = 20;

function estimateReadTime(content: string) {
  const words = stripRichTextToPlainText(content).trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

function formatDate(dateValue: string | null) {
  if (!dateValue) return "Draft";
  return new Date(dateValue).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function Blog() {
  const [posts, setPosts] = useState<BlogPostListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const loadPosts = async (offset = 0, append = false) => {
    const loadingSetter = append ? setLoadingMore : setLoading;
    loadingSetter(true);

    try {
      const { data, error, count } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, meta_description, cover_image_url, author_name, published_at, created_at, content", {
          count: "exact",
        })
        .eq("published", true)
        .order("published_at", { ascending: false, nullsFirst: false })
        .range(offset, offset + PAGE_SIZE - 1);

      if (error) throw error;

      setPosts((prev) => (append ? [...prev, ...((data || []) as BlogPostListItem[])] : ((data || []) as BlogPostListItem[])));
      setTotalCount(count || 0);
    } catch (error) {
      console.error("Failed to load blog posts", error);
    } finally {
      loadingSetter(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const hasMore = posts.length < totalCount;

  return (
    <SiteLayout>
      <SEO
        title="Blog"
        description="Articles, guides, and FAQs from Splitkart on group gifting, split payments, and conversion-friendly commerce."
        url="/blog"
      />
      <StructuredData
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Splitkart Blog",
          description: "Articles, guides, and FAQs about group gifting and split payments.",
          url: "https://splitkart.app/blog",
          isPartOf: { "@id": "https://splitkart.app/#website" },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: posts.map((post, index) => ({
              "@type": "ListItem",
              position: index + 1,
              url: `https://splitkart.app/blog/${post.slug}`,
              name: post.title,
            })),
          },
        }}
      />

      <section className="section bg-gradient-hero">
        <div className="container-tight text-center">
          <span className="eyebrow">Blog</span>
          <h1 className="mt-5 text-balance text-4xl font-bold md:text-6xl">Guides on Split Payments & Group Gifting</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            For Shopify and WooCommerce merchants: conversion, checkout, and customer behavior. For shoppers: how to split a gift without the awkward money chase.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-tight">
          {loading ? (
            <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card p-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading blog posts...
            </div>
          ) : posts.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card p-12 text-center">
              <h2 className="text-2xl font-semibold">Blog coming soon</h2>
              <p className="mt-3 text-muted-foreground">
                We haven’t published any posts yet, but the CMS is ready.
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {posts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:border-primary/30 hover:shadow-elevated"
                  >
                    <div className="aspect-[16/10] bg-surface">
                      {post.cover_image_url ? (
                        <img
                          src={post.cover_image_url}
                          alt={post.title}
                          width="1200"
                          height="750"
                          className="h-full w-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center bg-gradient-to-br from-primary/10 via-background to-primary/5 px-6 text-center text-lg font-semibold text-primary">
                          Splitkart Blog
                        </div>
                      )}
                    </div>

                    <div className="flex flex-1 flex-col p-7">
                      <div className="flex items-center justify-between gap-3">
                        <span className="rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-accent-foreground">
                          {post.author_name || "Splitkart"}
                        </span>
                        <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
                      </div>

                      <h2 className="mt-5 text-lg font-bold leading-snug group-hover:text-primary">{post.title}</h2>
                      <p className="mt-2 flex-1 text-sm text-muted-foreground">
                        {post.excerpt || post.meta_description || "Read the full article on Splitkart."}
                      </p>
                      <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{formatDate(post.published_at || post.created_at)}</span>
                        <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                        <span>{estimateReadTime(post.content)} min read</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {hasMore && (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    onClick={() => loadPosts(posts.length, true)}
                    disabled={loadingMore}
                    className="inline-flex items-center rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-60"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      "Load more"
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}

export default Blog;
